import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { copyText } from '../../../utils/copyText';

// Highlights the [placeholders] the owner has to fill in.
const withPlaceholders = (text) =>
  text.split(/(\[[^\]]+\])/g).map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <span key={i} className="rp-placeholder">
        {part}
      </span>
    ) : (
      part
    )
  );

const CopyButton = ({ text, label = 'Copiar' }) => {
  const [state, setState] = useState('idle'); // idle | copied | failed

  const handleCopy = async () => {
    const ok = await copyText(text);
    setState(ok ? 'copied' : 'failed');
    setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="no-print inline-flex shrink-0 items-center gap-2 rounded-full border border-[var(--lp-line)] px-3.5 py-1.5 text-sm font-semibold transition-colors hover:border-[var(--lp-text)]"
    >
      {state === 'copied' ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      <span aria-live="polite">
        {state === 'copied' ? 'Copiado' : state === 'failed' ? 'No se pudo copiar' : label}
      </span>
    </button>
  );
};

const AssetCard = ({ title, where, text, limit, mono = false, className = '' }) => (
  <div className={`rp-keep flex flex-col rounded-[24px] border border-[var(--lp-line)] p-6 sm:p-7 ${className}`}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="mt-1 text-sm text-[var(--lp-muted)]">{where}</p>
      </div>
      <CopyButton text={text} />
    </div>
    <div
      className={`mt-5 whitespace-pre-wrap rounded-2xl bg-[var(--lp-surface)] p-4 leading-relaxed ${
        mono ? 'lp-mono overflow-x-auto text-[13px]' : 'text-[15px]'
      }`}
    >
      {mono ? text : withPlaceholders(text)}
    </div>
    {limit && (
      <p className="mt-3 text-xs text-[var(--lp-muted)]">
        {text.length} de {limit} caracteres como máximo
      </p>
    )}
  </div>
);

const DoneForYou = ({ report }) => {
  const { assets } = report;
  const faqText = assets.faq.map((item) => `${item.question}\n${item.answer}`).join('\n\n');

  return (
    <div className="space-y-6">
      <p className="flex flex-wrap items-center gap-2 text-sm text-[var(--lp-muted)]">
        Lo que ves
        <span className="rp-placeholder">[entre corchetes]</span>
        son datos de tu negocio que no conocemos: rellénalos antes de publicar.
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        <AssetCard
          title="Título de tu página"
          where="En el título de la página principal de tu web (etiqueta <title>)."
          text={assets.pageTitle}
          limit={60}
        />
        <AssetCard
          title="Descripción para buscadores"
          where="En la descripción de la página principal (meta description)."
          text={assets.metaDescription}
          limit={155}
        />
      </div>

      <AssetCard
        title="Descripción para tu ficha de Google"
        where="En tu ficha de Google, apartado «Descripción» (lo ve cualquiera que te busque en Maps)."
        text={assets.googleBusinessDescription}
        limit={750}
      />

      <div className="rp-keep rounded-[24px] border border-[var(--lp-line)] p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold tracking-[-0.01em]">Preguntas frecuentes para tu web</h3>
            <p className="mt-1 text-sm text-[var(--lp-muted)]">
              Responden a lo que tus clientes le preguntan a la IA. Ponlas en una página o sección de tu web.
            </p>
          </div>
          <CopyButton text={faqText} label="Copiar todas" />
        </div>
        <dl className="mt-5 divide-y divide-[var(--lp-line)] rounded-2xl bg-[var(--lp-surface)] px-4">
          {assets.faq.map((item, i) => (
            <div key={i} className="py-4">
              <dt className="font-semibold">{item.question}</dt>
              <dd className="mt-1.5 leading-relaxed text-[var(--lp-muted)]">{withPlaceholders(item.answer)}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <AssetCard
          title="Mensaje para pedir reseñas"
          where="Mándalo por WhatsApp a tus clientes habituales, o imprímelo junto a un QR."
          text={assets.reviewRequestMessage}
        />
        <AssetCard
          title="Datos de negocio local (código)"
          where="Pásaselo a quien lleve tu web: va dentro del <head> de la página principal."
          text={assets.localBusinessJsonLd}
          mono
        />
      </div>
    </div>
  );
};

export default DoneForYou;
